import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {
  ActionIcon,
  Box,
  Button as MantineButton,
  createStyles,
  Group,
  Loader,
  Text,
  Tooltip,
} from '@mantine/core';
import {useId, useMergedRef} from '@mantine/hooks';
import {captureException} from '@sentry/browser';
import {css, CSSPropertiesComplete, StyleSheet} from 'aphrodite';
import React, {
  useState,
  KeyboardEvent,
  ChangeEvent,
  ClipboardEvent,
  useRef,
  useCallback,
  useMemo,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import FolderSelector from 'file-browser/FolderSelector';
import {AgentType, ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import {AgentToolbar} from 'llm/chat-library/AgentToolbar';
import AssistantModeDropdown from 'llm/chat-library/AssistantModeDropdown';
import * as ChatActions from 'llm/chat-library/ChatActions';
import {
  AgentApprovalUpdateSource,
  AssistantEffort,
  CitationGenerationMode,
  type CreateChatOptions,
} from 'llm/chat-library/ChatDAO';
import {
  AgentTypeError,
  ChatDraftSettings,
  ChatDraftStatus,
  getAssistantEffortPreference,
  getDraftState,
  makeNewDraftState,
  setDraftState,
  removeFileFromDraft,
  setAssistantEffortPreference,
  setPublicDataEnabledPreference,
  useChatDraftState,
} from 'llm/chat-library/ChatDraftState';
import {ChatSelectedFile} from 'llm/chat-library/ChatLibraryTypes';
import ContextChips from 'llm/chat-library/ContextChips';
import CoScientistAgentWorkspaceFolderChip from 'llm/chat-library/CoScientistAgentWorkspaceFolderChip';
import {TargetItemInfo} from 'llm/chat-library/getTargetItem';
import type {CreateChatResult} from 'llm/chat-library/sidepanel/ChatRouter';
import {
  doesAgentTypeSupportRealtimeVoice,
  getAgentTypePlaceholderText,
  getChatTypesForNewAgentChat,
  getChatTypeForNewAgentChat,
} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import TargetItemChip from 'llm/chat-library/TargetItemChip';
import {
  AI_GRAY_200,
  AI_GRAY_425,
  CHAT_CONTENT_MAX_WIDTH_PX,
  CHAT_INPUT_TEXTAREA_MAX_HEIGHT_PX,
  CHAT_INPUT_TEXTAREA_MIN_HEIGHT_IN_CHAT_PX,
  CHAT_INPUT_TEXTAREA_MIN_HEIGHT_NEW_CHAT_PX,
  CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX,
} from 'llm/chat-library/ui/AIStyleConstants';
import recordChatUseCaseRun from 'llm/chat-library/use-cases/recordChatUseCaseRun';
import {
  isVoiceInputActive,
  VoiceInputAction,
  VoiceInputDecorations,
  VoiceInputStatus,
  VoiceToolProcessingStatus,
  VOICE_CONTROL_SIZE_PX,
} from 'llm/chat-library/VoiceInputControl';
import {DISMISSED_AUTOMATIC_FULL_ACTIONS_NOTICE_PREFERENCE} from 'llm/chat-library/VoicePreferences';
import {useMCPEnabledServersOptional} from 'llm/mcp-client-library/MCPEnabledServersContext';
import {VoiceUIController, VoiceUIState} from 'llm/realtime-voice/VoiceUIController';
import notifications from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHTER} from 'ui-library/Colors';
import usePreference from 'ui-library/hooks/usePreference';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import DropTarget from 'util/drag-and-drop/DropTarget';
import * as Net from 'util/Net';

import {
  cancelRealtimeVoiceChatHandoff,
  claimRealtimeVoiceChatActivation,
  claimRealtimeVoiceStart,
  createRealtimeVoiceChatHandoffScope,
  createRealtimeVoiceChatHandoff,
  getPendingRealtimeVoiceChatHandoff,
  hasUnclaimedRealtimeVoiceChatHandoff,
  type RealtimeVoiceChatHandoff,
} from './RealtimeVoiceChatHandoff';

const INACTIVE_MICROPHONE_LEVEL: VoiceUIController['microphoneLevel'] = {
  getSnapshot: () => 0,
  subscribe: () => () => {},
};

/** Transparent gutter that grows the send control's footprint to match the Voice controls. */
const SEND_CONTROL_GUTTER_PX = (VOICE_CONTROL_SIZE_PX - CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX) / 2;

export const CONTEXT_CHAT_INPUT_PLACEHOLDER = 'Ask questions about your data...';

type VoiceStartOperationToken = number;

interface ChatVoiceStartLifecycleIdentity {
  kind: 'chat';
  chatSessionId: string | null;
  startVoiceSession: VoiceUIController['start'] | undefined;
}

interface NewChatVoiceStartLifecycleIdentity {
  kind: 'new-chat';
  voiceHandoffScopeId: string;
  agentType: AgentType | null;
  startVoiceFromNewChat: () => Promise<void>;
}

export type VoiceStartLifecycleIdentity =
  | ChatVoiceStartLifecycleIdentity
  | NewChatVoiceStartLifecycleIdentity;

/**
 * Guards an asynchronous Voice start against lifecycle changes.
 *
 * `begin` advances the generation, so at most the newest start token is current. Changing any
 * field in the typed lifecycle identity or unmounting the owner also advances the generation.
 *
 * The owner must live exactly as long as a pending start may continue. Chat and NewChatInput own
 * the guard across their child ChatInput lifecycle; a standalone ChatInput owns a local guard.
 */
export interface VoiceStartLifecycleGuard {
  begin: () => VoiceStartOperationToken;
  isCurrent: (token: VoiceStartOperationToken) => boolean;
}

interface MutableVoiceStartLifecycleGuard extends VoiceStartLifecycleGuard {
  updateIdentity: (identity: VoiceStartLifecycleIdentity) => void;
  invalidate: () => void;
}

function isSameVoiceStartLifecycleIdentity(
  current: VoiceStartLifecycleIdentity | null,
  next: VoiceStartLifecycleIdentity
): boolean {
  if (current?.kind === 'chat' && next.kind === 'chat') {
    return (
      current.chatSessionId === next.chatSessionId && current.startVoiceSession === next.startVoiceSession
    );
  }
  if (current?.kind === 'new-chat' && next.kind === 'new-chat') {
    return (
      current.voiceHandoffScopeId === next.voiceHandoffScopeId &&
      current.agentType === next.agentType &&
      current.startVoiceFromNewChat === next.startVoiceFromNewChat
    );
  }
  return false;
}

function createVoiceStartLifecycleGuard(): MutableVoiceStartLifecycleGuard {
  let generation = 0;
  let identity: VoiceStartLifecycleIdentity | null = null;

  const invalidate = (): void => {
    generation += 1;
  };

  return {
    begin: () => {
      invalidate();
      return generation;
    },
    isCurrent: (token) => token === generation,
    updateIdentity: (nextIdentity) => {
      if (!isSameVoiceStartLifecycleIdentity(identity, nextIdentity)) {
        identity = nextIdentity;
        invalidate();
      }
    },
    invalidate,
  };
}

export function useVoiceStartLifecycleGuard(identity: VoiceStartLifecycleIdentity): VoiceStartLifecycleGuard {
  const guardRef = useRef<MutableVoiceStartLifecycleGuard | null>(null);
  if (guardRef.current == null) {
    guardRef.current = createVoiceStartLifecycleGuard();
  }
  const guard = guardRef.current;
  guard.updateIdentity(identity);

  useEffect(
    () => () => {
      guard.invalidate();
    },
    [guard]
  );

  return guard;
}

export interface ChatInputProps {
  chatSessionId: string | null;
  voiceUIController?: VoiceUIController;
  agentTypeError?: AgentTypeError;
  inputText: string;
  setInputText: (message: string) => void;
  publicDataEnabled: boolean;
  setPublicDataEnabled: (enabled: boolean) => void;
  citationGenerationMode: CitationGenerationMode;
  setCitationGenerationMode: (mode: CitationGenerationMode) => void;
  effort?: AssistantEffort | null;
  setEffort?: (effort: AssistantEffort) => void;
  onSubmit: () => void;
  /** When true, show a stop control instead of send while the assistant is responding. */
  showStopButton?: boolean;
  onInterrupt?: () => void;
  isInterrupting?: boolean;
  inputDisabled: boolean;
  /** All selected files (uploading, failed, completed) */
  selectedFiles: Array<ChatSelectedFile>;
  /** Called when user selects files to upload */
  onSelectFiles: (files: Array<File>) => void;
  /** Called when user removes a file (works for any status) */
  onRemoveFile: (key: string) => void;
  /**
   * The current agent type.
   * - Can be null while settings are still loading.
   * - When null, submission is blocked but the input is still usable.
   */
  agentType: AgentType | null;
  setAgentType: (agentType: AgentType | null) => void;
  allowedChatTypes: Array<ChatType>;
  /** Target item for Compose-over-entries chats. Shows a chip indicator when present. */
  targetItem: TargetItemInfo | null;
  /** Item IDs attached to the draft as context, rendered as chips and applied on first send. */
  contextIds?: Array<string>;
  removeContextId?: (id: string) => void;
  /** Persisted agent workspace folder ID for existing co-scientist chats. */
  agentWorkspaceFolderId?: string | null;
  setAgentWorkspaceFolderId?: (agentWorkspaceFolderId: string | null) => void;
  /**
   * Agent workspace folder ID for any agent type, used only to name the folder in the "Full
   * access" chip tooltip. Unlike agentWorkspaceFolderId, not scoped to co-scientist.
   */
  fullAccessFolderId?: string | null;
  /**
   * Sets the agent workspace folder for an *existing* chat via the network (see
   * ChatDAO.updateChatAgentWorkspaceFolder). Only meaningful for AI Scientist chats that have lost
   * their folder (e.g. after revoking full access) - lets the user recover it without starting a
   * new chat. Undefined for new chats, which only ever hold the folder as local draft state.
   */
  onSetAgentWorkspaceFolder?: (agentWorkspaceFolderId: string) => Promise<boolean>;
  /** MCP server IDs selected for this chat. Undefined means "all enabled" (default). */
  selectedMcpServerIds?: Array<string> | undefined;
  setSelectedMcpServerIds?: (serverIds: Array<string> | undefined) => void;
  /**
   * Whether write-action approval is currently in effect: granted on the server for an existing
   * chat, or held as intent in draft state for a new chat (see ChatDraftSettings.newChatFullAccess).
   */
  fullAccessEnabled?: boolean;
  /** Whether this chat holds the exact WRITE_ACTIONS grant required to start Voice. */
  writeActionsEnabled?: boolean;
  /** Grant write-action approval, either immediately on the server or by recording draft intent. */
  onGrantFullAccess?: (source: AgentApprovalUpdateSource) => Promise<boolean>;
  /** Revoke write-action approval, either immediately on the server or by clearing draft intent. */
  onRevokeFullAccess?: (source: AgentApprovalUpdateSource) => Promise<boolean>;
  /**
   * Background-agent-queue toggle for new AI Scientist chats. Only passed for new chats;
   * when undefined the Legacy Data Import switch is hidden (it affects creation only).
   */
  useBackgroundAgentQueue?: boolean;
  setUseBackgroundAgentQueue?: (enabled: boolean) => void;
  /** When true, styles the input text as muted (used for preview text from progressive disclosure). */
  isPreviewText?: boolean;
  onStartVoiceFromNewChat?: () => Promise<void>;
  onCancelVoiceHandoff?: () => void;
  isStartingVoiceFromNewChat?: boolean;
  pendingVoiceToolCount?: number;
  voiceStartLifecycleGuard?: VoiceStartLifecycleGuard;
  /**
   * Hides controls that don't apply to messaging a saved agent (agent-type picker, Mode, and Voice),
   * while retaining the Full actions chip and other applicable controls. Set by the Agent-homepage DM
   * composer; also auto-enabled for follow-up turns in any routine-backed chat.
   */
  restrictToAgentControls?: boolean;
  /** Hides Citations when the caller cannot apply its setting to the submitted message. */
  hideCitationsControl?: boolean;
  /** Hides connectors when the caller must not send the user's MCP servers. */
  hideMcpControl?: boolean;
  /** Disables user-authored changes when the backing Agent's run-as mode is read-only. */
  routineDisabled?: boolean;
  /** Requires non-whitespace text even when the draft has completed attachments. */
  requireTextForSubmit?: boolean;
  /** Lets callers focus the compose textarea imperatively. */
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
}

export interface NewChatInputHandle {
  submitWithText: (text: string) => Promise<boolean>;
  /** Replaces the draft text with editable text and focuses the input. */
  setText: (text: string) => void;
  setPreviewText: (text: string) => void;
  clearPreviewText: () => void;
}

export function AIWarning(): JSX.Element {
  return (
    <div className={css(styles.warningMessage)} {...getTestProps({component: 'AIWarning', element: 'root'})}>
      AI can make mistakes. Always check results.
    </div>
  );
}

/**
 * Chat input for new chats. Uses draft state initialized by openDraftChat.
 */
export const NewChatInput = React.forwardRef<
  NewChatInputHandle,
  {
    onCreateChat: (result: CreateChatResult) => void;
    onSubmittingChange?: (isSubmitting: boolean) => void;
    isPreviewText?: boolean;
    onHasUserInputChange?: (hasInput: boolean) => void;
    voiceUIController?: VoiceUIController;
    voiceHandoffScopeId?: string;
  }
>((props, ref) => {
  const {
    onCreateChat,
    onSubmittingChange,
    isPreviewText,
    onHasUserInputChange,
    voiceUIController,
    voiceHandoffScopeId: ownerScopeId,
  } = props;
  const [voiceHandoffScopeId] = useState(() => ownerScopeId ?? createRealtimeVoiceChatHandoffScope());
  const [agentWorkspaceFolderId, setAgentWorkspaceFolderId] = useState<string | null>(null);
  const [useBackgroundAgentQueue, setUseBackgroundAgentQueue] = useState<boolean>(false);
  const [isStartingVoiceFromNewChat, setIsStartingVoiceFromNewChat] = useState(false);
  const isMountedRef = useRef(false);
  // Imperative setPreviewText updates draft inputText before the parent commits isPreviewText. Skip
  // onHasUserInputChange until that prop catches up so progressive disclosure stays visible.
  const isPreviewTextPendingRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const onCreateChatRef = useRef(onCreateChat);
  const voiceHandoffPromiseRef = useRef<Promise<void> | null>(null);
  const newChatOperationRef = useRef<'voice' | 'text' | null>(null);
  const voiceHandoffStoppedRef = useRef(false);
  onCreateChatRef.current = onCreateChat;
  // Draft state is initialized by openDraftChat with agentType and publicDataEnabled
  const controls = useChatInputControls(null);
  const {inputText, agentType, hasUploadingFiles, selectedFiles} = controls;
  const voiceWriteActionsConsentRef = useRef(controls.newChatFullAccess);

  const completeVoiceHandoff = useCallback(
    (handoffPromise: Promise<RealtimeVoiceChatHandoff | null>): Promise<void> => {
      if (newChatOperationRef.current === 'text') {
        return Promise.resolve();
      }
      if (newChatOperationRef.current === 'voice' && voiceHandoffPromiseRef.current != null) {
        return voiceHandoffPromiseRef.current;
      }
      newChatOperationRef.current = 'voice';
      setIsStartingVoiceFromNewChat(true);
      const completionPromise = handoffPromise
        .then((handoff) => {
          if (
            handoff == null ||
            !isMountedRef.current ||
            !claimRealtimeVoiceChatActivation(voiceHandoffScopeId, handoff)
          ) {
            return;
          }
          const draft = getDraftState(null) ?? makeNewDraftState(handoff.initialAgentType);
          setDraftState(
            handoff.chatSessionId,
            draft.settings == null
              ? makeNewDraftState(handoff.initialAgentType)
              : {
                  ...draft,
                  settings: {
                    ...draft.settings,
                    agentType: handoff.initialAgentType,
                  },
                }
          );
          setDraftState(null, null);
          onCreateChatRef.current({...handoff, keepCompact: true});
        })
        .finally(() => {
          if (voiceHandoffPromiseRef.current === completionPromise) {
            if (isMountedRef.current) {
              setIsStartingVoiceFromNewChat(false);
            }
            if (newChatOperationRef.current === 'voice') {
              newChatOperationRef.current = null;
            }
            voiceHandoffPromiseRef.current = null;
          }
        });
      voiceHandoffPromiseRef.current = completionPromise;
      return completionPromise;
    },
    [voiceHandoffScopeId]
  );

  useEffect(() => {
    isMountedRef.current = true;
    const pendingHandoff = getPendingRealtimeVoiceChatHandoff(voiceHandoffScopeId);
    if (pendingHandoff != null) {
      completeVoiceHandoff(pendingHandoff).catch((error: unknown) => {
        notifications.error(Net.getUserMessage(error) ?? 'Could not start Voice. Please try again.', {
          id: 'realtime-voice-create-error',
        });
      });
    }
    return () => {
      isMountedRef.current = false;
    };
  }, [completeVoiceHandoff, voiceHandoffScopeId]);

  const cancelVoiceHandoff = useCallback(() => {
    cancelRealtimeVoiceChatHandoff(voiceHandoffScopeId);
    voiceHandoffStoppedRef.current = true;
    setIsStartingVoiceFromNewChat(false);
  }, [voiceHandoffScopeId]);

  useEffect(() => {
    if (isPreviewText) {
      isPreviewTextPendingRef.current = false;
    }
    if (!isPreviewText && !isPreviewTextPendingRef.current) {
      onHasUserInputChange?.(inputText.length > 0);
    }
  }, [inputText, isPreviewText, onHasUserInputChange]);

  useEffect(() => {
    // Reset when the user switches away from AI Scientist. Clearing agentWorkspaceFolderId here is
    // required, not just tidy: fullAccessEnabled and the tooltip below both derive from it without
    // checking agentType, and getCreateChatOptions already omits the folder for non-CO_SCIENTIST
    // chats - so a stale folder from a previous CO_SCIENTIST selection would make the UI promise
    // Full access while actually submitting a Default-access chat.
    if (agentType !== AgentType.CO_SCIENTIST) {
      setUseBackgroundAgentQueue(false);
      setAgentWorkspaceFolderId(null);
    }
  }, [agentType]);

  const getCreateChatOptions = useCallback(
    (selectedAgentType: AgentType): CreateChatOptions => {
      if (selectedAgentType === AgentType.CO_SCIENTIST && agentWorkspaceFolderId != null) {
        return {
          [AgentType.CO_SCIENTIST]: {
            agentWorkspaceFolderId,
            useBackgroundAgentQueue: useBackgroundAgentQueue || undefined,
          },
        };
      }
      return {};
    },
    [agentWorkspaceFolderId, useBackgroundAgentQueue]
  );

  const {isSubmitting, sendMessage} = useNewChat({
    onCreateChat,
    onSubmittingChange,
    getCreateChatOptions,
  });

  const handleStartVoiceFromNewChat = useCallback((): Promise<void> => {
    if (voiceHandoffPromiseRef.current != null && !voiceHandoffStoppedRef.current) {
      return voiceHandoffPromiseRef.current;
    }
    if (newChatOperationRef.current === 'text') {
      return Promise.resolve();
    }
    if (agentType == null || !doesAgentTypeSupportRealtimeVoice(agentType)) {
      return Promise.resolve();
    }
    if (voiceHandoffStoppedRef.current) {
      voiceHandoffStoppedRef.current = false;
      voiceHandoffPromiseRef.current = null;
      newChatOperationRef.current = null;
    }
    return completeVoiceHandoff(
      createRealtimeVoiceChatHandoff(agentType, voiceHandoffScopeId, () =>
        ChatActions.createRealtimeVoiceChat(agentType)
      )
    );
  }, [agentType, completeVoiceHandoff, voiceHandoffScopeId]);
  const voiceStartLifecycleGuard = useVoiceStartLifecycleGuard({
    kind: 'new-chat',
    voiceHandoffScopeId,
    agentType,
    startVoiceFromNewChat: handleStartVoiceFromNewChat,
  });

  // For new chats, full access isn't granted on the server until the chat is actually created
  // (see ChatDraftSettings.newChatFullAccess): clicking "Full" here just records the intent in the
  // draft, and it's sent as part of creating the chat when the initial message is submitted. This
  // avoids creating a chat as a side effect of a plus-menu click.
  const handleGrantFullAccess = useCallback(
    (_source: AgentApprovalUpdateSource): Promise<boolean> => {
      voiceWriteActionsConsentRef.current = true;
      controls.setNewChatFullAccess(true);
      return Promise.resolve(true);
    },
    [controls]
  );

  // Clearing the folder here (not just the WRITE_ACTIONS intent) matches revokeFullAccess's
  // server-side behavior for existing chats (see update_chat_agent_approval): a folder alone also
  // confers "full access", so removing access must remove the folder too. This never hits the
  // network - see the module doc on newChatFullAccess.
  const handleRevokeFullAccess = useCallback((): Promise<boolean> => {
    voiceWriteActionsConsentRef.current = false;
    controls.setNewChatFullAccess(false);
    setAgentWorkspaceFolderId(null);
    return Promise.resolve(true);
  }, [controls, setAgentWorkspaceFolderId]);

  const handleSubmitMessage = useCallback(async () => {
    const isMissingAgentWorkspaceFolder =
      agentType === AgentType.CO_SCIENTIST && agentWorkspaceFolderId == null;
    if (
      (!inputText.trim() && selectedFiles.length === 0) ||
      isSubmitting ||
      isStartingVoiceFromNewChat ||
      newChatOperationRef.current != null ||
      hasUnclaimedRealtimeVoiceChatHandoff(voiceHandoffScopeId) ||
      !agentType ||
      hasUploadingFiles ||
      isMissingAgentWorkspaceFolder
    ) {
      return;
    }
    // Draft state already has everything - just submit
    newChatOperationRef.current = 'text';
    try {
      await sendMessage();
    } finally {
      if (newChatOperationRef.current === 'text') {
        newChatOperationRef.current = null;
      }
    }
  }, [
    agentType,
    agentWorkspaceFolderId,
    inputText,
    isSubmitting,
    isStartingVoiceFromNewChat,
    hasUploadingFiles,
    selectedFiles.length,
    sendMessage,
    voiceHandoffScopeId,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      submitWithText: async (text: string) => {
        if (
          newChatOperationRef.current != null ||
          hasUnclaimedRealtimeVoiceChatHandoff(voiceHandoffScopeId)
        ) {
          return false;
        }
        const draft = getDraftState(null);
        if (!draft || !draft.settings?.agentType) {
          return false;
        }
        newChatOperationRef.current = 'text';
        setDraftState(null, {
          ...draft,
          status: ChatDraftStatus.DRAFT,
          message: {...draft.message, text},
        });
        try {
          await sendMessage();
          return true;
        } finally {
          if (newChatOperationRef.current === 'text') {
            newChatOperationRef.current = null;
          }
        }
      },
      setText: (text: string) => {
        isPreviewTextPendingRef.current = false;
        controls.setInputText(text);
        textareaRef.current?.focus();
      },
      setPreviewText: (text: string) => {
        isPreviewTextPendingRef.current = true;
        controls.setInputText(text);
      },
      clearPreviewText: () => {
        isPreviewTextPendingRef.current = false;
        controls.setInputText('');
      },
    }),
    [sendMessage, controls, voiceHandoffScopeId]
  );

  const isPreviewTextActive = isPreviewText || isPreviewTextPendingRef.current;

  return (
    <ChatInput
      chatSessionId={null}
      agentTypeError={controls.agentTypeError}
      {...controls}
      onSubmit={handleSubmitMessage}
      inputDisabled={isSubmitting || isStartingVoiceFromNewChat}
      allowedChatTypes={getChatTypesForNewAgentChat()}
      agentWorkspaceFolderId={agentWorkspaceFolderId}
      setAgentWorkspaceFolderId={setAgentWorkspaceFolderId}
      fullAccessFolderId={agentWorkspaceFolderId}
      useBackgroundAgentQueue={useBackgroundAgentQueue}
      setUseBackgroundAgentQueue={setUseBackgroundAgentQueue}
      targetItem={null}
      // newChatFullAccess is only ever set via handleGrantFullAccess, which PlusMenu already gates
      // on approvals being enabled - so it needs no extra gating here. The folder can be picked
      // regardless of approvals state (the folder picker itself isn't gated), so folder presence
      // must be gated on approvals explicitly here: a user without approvals enabled should see
      // default access with their folder, not full access they have no way to grant or revoke.
      fullAccessEnabled={
        controls.newChatFullAccess ||
        (BenchlingConfig.LLM_ENABLE_AGENT_APPROVALS && agentWorkspaceFolderId != null)
      }
      writeActionsEnabled={voiceWriteActionsConsentRef.current}
      onGrantFullAccess={handleGrantFullAccess}
      onRevokeFullAccess={handleRevokeFullAccess}
      isPreviewText={isPreviewTextActive}
      voiceUIController={voiceUIController}
      onStartVoiceFromNewChat={handleStartVoiceFromNewChat}
      onCancelVoiceHandoff={cancelVoiceHandoff}
      isStartingVoiceFromNewChat={isStartingVoiceFromNewChat}
      voiceStartLifecycleGuard={voiceStartLifecycleGuard}
      textareaRef={textareaRef}
    />
  );
});

export interface ChatInputControls {
  agentTypeError?: AgentTypeError;
  inputText: string;
  setInputText: (message: string) => void;
  /** All selected files (uploading, failed, completed) */
  selectedFiles: Array<ChatSelectedFile>;
  /** Item IDs attached to the draft as context, rendered as chips and applied on first send. */
  contextIds?: Array<string>;
  removeContextId?: (id: string) => void;
  /** Whether any files are still uploading */
  hasUploadingFiles: boolean;
  /** Whether the draft message is pending (status is PENDING) */
  isPending: boolean;
  /** Called when user selects files to upload */
  onSelectFiles: (files: Array<File>) => void;
  /** Called when user removes a file (works for any status) */
  onRemoveFile: (key: string) => void;
  /**
   * The current agent type.
   * - Can be null while settings are still loading.
   * - For new chats, this comes from draft state initialized by openDraftChat.
   * - For existing chats, this comes from draft state seeded by useMessageThread.
   */
  agentType: AgentType | null;
  setAgentType: (agentType: AgentType | null) => void;
  publicDataEnabled: boolean;
  setPublicDataEnabled: (publicDataEnabled: boolean) => void;
  citationGenerationMode: CitationGenerationMode;
  setCitationGenerationMode: (citationGenerationMode: CitationGenerationMode) => void;
  effort: AssistantEffort | null;
  setEffort: (effort: AssistantEffort) => void;
  /** MCP server IDs selected for this chat. Undefined means "all enabled" (default). */
  selectedMcpServerIds: Array<string> | undefined;
  setSelectedMcpServerIds: (serverIds: Array<string> | undefined) => void;
  /**
   * Requested write-action approval for the chat this draft will create. Only meaningful for new
   * chats (chatId is null) - see ChatDraftSettings.newChatFullAccess.
   */
  newChatFullAccess: boolean;
  setNewChatFullAccess: (enabled: boolean) => void;
}

// TODO: Unify with useMessageThread.
function useNewChat({
  onCreateChat,
  onSubmittingChange,
  getCreateChatOptions,
}: {
  onCreateChat: (result: CreateChatResult) => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
  getCreateChatOptions?: (agentType: AgentType) => CreateChatOptions;
}): {
  isSubmitting: boolean;
  sendMessage: () => Promise<void>;
} {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const mcpEnabledServers = useMCPEnabledServersOptional();
  const mcpServerIds = mcpEnabledServers?.enabledServerIds;

  const sendMessage = useCallback(async () => {
    // Read from draft state - it should already have everything from user input
    const draft = getDraftState(null);
    if (!draft) {
      return;
    }

    const agentType = draft.settings?.agentType;
    if (!agentType) {
      // This shouldn't happen - caller should check agentType before calling sendMessage
      return;
    }

    const chatType = getChatTypeForNewAgentChat(agentType);
    const createChatOptions = getCreateChatOptions?.(agentType) ?? {};

    setIsSubmitting(true);
    onSubmittingChange?.(true);
    try {
      const {chatSessionId} = await ChatActions.createChatWithDraftMessage(chatType, {
        createChatOptions,
        createParams: {mcpServerIds},
      });
      if (draft.stagedChatUseCase != null) {
        recordChatUseCaseRun(draft.stagedChatUseCase.chatUseCaseId, {
          categoryId: draft.stagedChatUseCase.categoryId,
          source: draft.stagedChatUseCase.source,
          campaign: draft.stagedChatUseCase.campaign,
        });
      }
      onCreateChat({chatSessionId, chatType, initialAgentType: agentType});
    } finally {
      setIsSubmitting(false);
      onSubmittingChange?.(false);
    }
  }, [getCreateChatOptions, onCreateChat, onSubmittingChange, mcpServerIds]);

  return {isSubmitting, sendMessage};
}

/**
 * Custom hook for managing related input state.
 *
 * All draft state (text, files, settings) is persisted in global state via ChatDraftState.
 * This includes files being uploaded - they persist across component unmount/remount.
 *
 * For new chats: Draft state is initialized by openDraftChat with agentType and publicDataEnabled.
 * For existing chats: Draft state is seeded by useMessageThread from the loaded thread.
 *
 * @param chatId - The chat session ID, or null for new chats.
 */
export function useChatInputControls(chatId: string | null): ChatInputControls {
  // Draft state is the single source of truth for everything.
  // useChatDraftState always returns a draft state object (never null).
  // For new chats, it includes the default agent type so the input is immediately usable.
  const [draftState, setDraft] = useChatDraftState(chatId);

  // Derive values from draft state.
  const agentType = draftState.settings?.agentType ?? null;
  const publicDataEnabled = draftState.settings?.publicDataEnabled ?? false;
  const citationGenerationMode = draftState.settings?.citationGenerationMode ?? CitationGenerationMode.AUTO;
  const effort = draftState.settings?.effort ?? null;
  const selectedMcpServerIds = draftState.settings?.selectedMcpServerIds;
  const newChatFullAccess = draftState.settings?.newChatFullAccess ?? false;
  const agentTypeError = draftState.agentTypeError;
  // When status is PENDING, the message is being sent - show empty input
  const inputText = draftState.status === ChatDraftStatus.PENDING ? '' : draftState.message.text;
  const selectedFiles = useMemo(() => draftState.message.selectedFiles, [draftState.message.selectedFiles]);
  const hasUploadingFiles = selectedFiles.some((f) => f.completedUpload == null);

  // Helper to update the draft state.
  // This only updates fields that are explicitly provided - it does NOT fill in defaults.
  const updateDraft = useCallback(
    (updates: Partial<ChatDraftSettings & {text: string; contextIds: Array<string> | undefined}>) => {
      // Don't overwrite PENDING status - sendMessage is handling this
      if (draftState.status === ChatDraftStatus.PENDING) {
        return;
      }

      // Determine the new settings.
      // - If settings was already defined, preserve it (with updated values)
      // - If settings was undefined and we're setting an agentType, create settings
      // - If settings was undefined and no agentType, keep undefined (still loading)
      const newAgentType = updates.agentType ?? draftState.settings?.agentType;
      const newPublicDataEnabled =
        updates.publicDataEnabled ?? draftState.settings?.publicDataEnabled ?? false;
      const newCitationGenerationMode =
        updates.citationGenerationMode ?? draftState.settings?.citationGenerationMode;
      const newEffort = updates.effort ?? draftState.settings?.effort;
      // selectedMcpServerIds uses explicit undefined to mean "all enabled servers"
      const newSelectedMcpServerIds = Object.hasOwn(updates, 'selectedMcpServerIds')
        ? updates.selectedMcpServerIds
        : draftState.settings?.selectedMcpServerIds;
      const newNewChatFullAccess = updates.newChatFullAccess ?? draftState.settings?.newChatFullAccess;
      const newSettings =
        newAgentType != null
          ? {
              agentType: newAgentType,
              publicDataEnabled: newPublicDataEnabled,
              citationGenerationMode: newCitationGenerationMode,
              effort: newEffort,
              selectedMcpServerIds: newSelectedMcpServerIds,
              newChatFullAccess: newNewChatFullAccess,
            }
          : undefined;

      setDraft({
        status: ChatDraftStatus.DRAFT,
        message: {
          text: updates.text ?? draftState.message.text,
          selectedFiles: draftState.message.selectedFiles,
        },
        settings: newSettings,
        // Carry context forward so it survives edits and is still attached on first send.
        contextIds: Object.hasOwn(updates, 'contextIds') ? updates.contextIds : draftState.contextIds,
        agentTypeError:
          // Clear agentTypeError if the agent type has changed
          updates.agentType != null && updates.agentType !== draftState.settings?.agentType
            ? undefined
            : draftState.agentTypeError,
        stagedChatUseCase: draftState.stagedChatUseCase,
        originSearchQuery: draftState.originSearchQuery,
      });
    },
    [draftState, setDraft]
  );

  const setInputText = useCallback(
    (text: string) => {
      updateDraft({text});
    },
    [updateDraft]
  );

  const setAgentType = useCallback(
    (newAgentType: AgentType | null) => {
      if (newAgentType != null) {
        updateDraft({
          agentType: newAgentType,
          effort:
            chatId == null && newAgentType === AgentType.ASSISTANT
              ? (draftState.settings?.effort ?? getAssistantEffortPreference())
              : draftState.settings?.effort,
        });
      }
    },
    [chatId, draftState.settings?.effort, updateDraft]
  );

  const setPublicDataEnabled = useCallback(
    (enabled: boolean) => {
      setPublicDataEnabledPreference(enabled);
      updateDraft({publicDataEnabled: enabled});
    },
    [updateDraft]
  );

  const setCitationGenerationMode = useCallback(
    (mode: CitationGenerationMode) => {
      updateDraft({citationGenerationMode: mode});
    },
    [updateDraft]
  );

  const setEffort = useCallback(
    (newEffort: AssistantEffort) => {
      setAssistantEffortPreference(newEffort);
      updateDraft({effort: newEffort});
    },
    [updateDraft]
  );

  const setSelectedMcpServerIds = useCallback(
    (serverIds: Array<string> | undefined) => {
      updateDraft({selectedMcpServerIds: serverIds});
    },
    [updateDraft]
  );

  const setNewChatFullAccess = useCallback(
    (enabled: boolean) => {
      updateDraft({newChatFullAccess: enabled});
    },
    [updateDraft]
  );

  const removeContextId = useCallback(
    (id: string) => {
      const remaining = (draftState.contextIds ?? []).filter((contextId) => contextId !== id);
      updateDraft({contextIds: remaining.length > 0 ? remaining : undefined});
    },
    [draftState.contextIds, updateDraft]
  );

  // File management - delegates to ChatActions/ChatDraftState
  const onSelectFiles = useCallback(
    (files: Array<File>) => {
      ChatActions.selectFilesForDraft(chatId, files);
    },
    [chatId]
  );

  const onRemoveFile = useCallback(
    (key: string) => {
      removeFileFromDraft(chatId, key);
    },
    [chatId]
  );

  const isPending = draftState?.status === ChatDraftStatus.PENDING;

  return {
    agentTypeError,
    inputText,
    setInputText,
    agentType,
    setAgentType,
    publicDataEnabled,
    setPublicDataEnabled,
    citationGenerationMode,
    setCitationGenerationMode,
    effort,
    setEffort,
    selectedMcpServerIds,
    setSelectedMcpServerIds,
    newChatFullAccess,
    setNewChatFullAccess,
    selectedFiles,
    contextIds: draftState.contextIds,
    removeContextId,
    hasUploadingFiles,
    isPending,
    onSelectFiles,
    onRemoveFile,
  };
}

export function ChatInput({
  chatSessionId,
  agentTypeError,
  inputText,
  setInputText,
  publicDataEnabled,
  setPublicDataEnabled,
  citationGenerationMode,
  setCitationGenerationMode,
  effort,
  setEffort,
  onSubmit,
  showStopButton = false,
  onInterrupt,
  isInterrupting = false,
  inputDisabled,
  selectedFiles,
  onSelectFiles,
  onRemoveFile,
  agentType,
  setAgentType,
  allowedChatTypes,
  agentWorkspaceFolderId,
  setAgentWorkspaceFolderId,
  fullAccessFolderId,
  onSetAgentWorkspaceFolder,
  targetItem,
  contextIds,
  removeContextId,
  selectedMcpServerIds,
  setSelectedMcpServerIds,
  fullAccessEnabled,
  writeActionsEnabled,
  onGrantFullAccess,
  onRevokeFullAccess,
  useBackgroundAgentQueue,
  setUseBackgroundAgentQueue,
  isPreviewText,
  voiceUIController,
  onStartVoiceFromNewChat,
  onCancelVoiceHandoff,
  isStartingVoiceFromNewChat = false,
  pendingVoiceToolCount = 0,
  voiceStartLifecycleGuard,
  restrictToAgentControls = false,
  hideCitationsControl = false,
  hideMcpControl = false,
  routineDisabled = false,
  requireTextForSubmit = false,
  textareaRef: externalTextareaRef,
}: ChatInputProps): JSX.Element {
  const {classes: attachmentClasses, cx: cxAttachments} = useAttachmentStyles();
  const {classes: voiceNoticeClasses} = useVoiceNoticeStyles();
  const {llmEnableAssistantEffortToggle} = useRolloutFlags();
  const {classes: voiceClasses, cx: cxVoice} = useVoiceLayoutStyles();
  const textareaMinHeightPx =
    chatSessionId == null
      ? CHAT_INPUT_TEXTAREA_MIN_HEIGHT_NEW_CHAT_PX
      : CHAT_INPUT_TEXTAREA_MIN_HEIGHT_IN_CHAT_PX;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mergedTextareaRef = useMergedRef(textareaRef, externalTextareaRef ?? null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [shouldFocusAfterVoice, setShouldFocusAfterVoice] = useState(false);
  const attachmentPanelId = `chat-input-attachments-${chatSessionId ?? 'new'}`;
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(false);
  const effectiveVoiceUIController = useMemo(
    () =>
      voiceUIController == null
        ? undefined
        : {
            ...voiceUIController,
            state: isStartingVoiceFromNewChat ? VoiceUIState.CONNECTING : voiceUIController.state,
            start: onStartVoiceFromNewChat ?? voiceUIController.start,
            stop: isStartingVoiceFromNewChat
              ? () => {
                  onCancelVoiceHandoff?.();
                  return Promise.resolve();
                }
              : voiceUIController.stop,
          },
    [isStartingVoiceFromNewChat, onCancelVoiceHandoff, onStartVoiceFromNewChat, voiceUIController]
  );
  const localVoiceStartLifecycleGuard = useVoiceStartLifecycleGuard({
    kind: 'chat',
    chatSessionId,
    startVoiceSession: effectiveVoiceUIController?.start,
  });
  const effectiveVoiceStartLifecycleGuard = voiceStartLifecycleGuard ?? localVoiceStartLifecycleGuard;
  const [dismissedAutomaticFullActionsNotice, setDismissedAutomaticFullActionsNotice] =
    usePreference<boolean>(DISMISSED_AUTOMATIC_FULL_ACTIONS_NOTICE_PREFERENCE);
  const automaticFullActionsNoticeLabelId = useId();
  const handleStartVoiceSession = useCallback(async (): Promise<void> => {
    if (effectiveVoiceUIController == null || writeActionsEnabled == null) {
      return;
    }
    const operationToken = effectiveVoiceStartLifecycleGuard.begin();
    // Try to enable Full actions when available; otherwise start in Default mode.
    if (
      writeActionsEnabled !== true &&
      BenchlingConfig.LLM_ENABLE_AGENT_APPROVALS &&
      onGrantFullAccess != null
    ) {
      try {
        await onGrantFullAccess(AgentApprovalUpdateSource.VOICE_START);
      } catch (error) {
        if (!Net.isServerRequestError(error)) {
          throw error;
        }
        // Starting Voice remains available in server-authorized Default mode when the optional
        // automatic Full actions grant cannot be completed.
        captureException(error);
      }
    }
    if (!effectiveVoiceStartLifecycleGuard.isCurrent(operationToken)) {
      return;
    }
    await effectiveVoiceUIController.start();
  }, [effectiveVoiceStartLifecycleGuard, effectiveVoiceUIController, onGrantFullAccess, writeActionsEnabled]);
  const authorizedVoiceUIController = useMemo(
    () =>
      effectiveVoiceUIController == null
        ? undefined
        : {...effectiveVoiceUIController, start: handleStartVoiceSession},
    [effectiveVoiceUIController, handleStartVoiceSession]
  );
  const isVoiceSessionActive =
    effectiveVoiceUIController != null && isVoiceInputActive(effectiveVoiceUIController.state);
  const isVoiceToolProcessing = pendingVoiceToolCount > 0;
  const inputControlsDisabled = inputDisabled || isVoiceToolProcessing || routineDisabled;
  const voiceStartDisabled = inputControlsDisabled || writeActionsEnabled == null;
  const conversationChangesDisabled = inputControlsDisabled || isVoiceSessionActive;
  const voicePresentationController = useMemo<VoiceUIController | undefined>(
    () =>
      isVoiceToolProcessing && !isVoiceSessionActive
        ? {
            state: VoiceUIState.AGENT_WORKING,
            isUserSpeaking: false,
            isAgentSpeaking: false,
            microphoneLevel: INACTIVE_MICROPHONE_LEVEL,
            start: () => Promise.resolve(),
            stop: () => Promise.resolve(),
          }
        : isVoiceToolProcessing && effectiveVoiceUIController != null
          ? {...effectiveVoiceUIController, state: VoiceUIState.AGENT_WORKING}
          : effectiveVoiceUIController,
    [effectiveVoiceUIController, isVoiceSessionActive, isVoiceToolProcessing]
  );
  const isVoicePresentationActive =
    voicePresentationController != null && isVoiceInputActive(voicePresentationController.state);
  const startOwnedVoiceSession = voiceUIController?.start;
  const handleVoiceStartError = useCallback((error: unknown): void => {
    setShouldFocusAfterVoice(true);
    notifications.error(Net.getUserMessage(error) ?? 'Could not start voice chat. Please try again.', {
      id: 'realtime-voice-start-error',
    });
  }, []);

  // Once the created chat has a Voice controller and known permissions,
  // claim its pending start exactly once.
  useEffect(() => {
    if (
      startOwnedVoiceSession == null ||
      writeActionsEnabled == null ||
      !claimRealtimeVoiceStart(chatSessionId)
    ) {
      return;
    }
    startOwnedVoiceSession().catch((error: unknown) => {
      handleVoiceStartError(error);
    });
  }, [chatSessionId, handleVoiceStartError, startOwnedVoiceSession, writeActionsEnabled]);

  useEffect(() => {
    if (!isVoiceSessionActive && !inputControlsDisabled && shouldFocusAfterVoice) {
      setShouldFocusAfterVoice(false);
      textareaRef.current?.focus({preventScroll: true});
    }
  }, [inputControlsDisabled, isVoiceSessionActive, shouldFocusAfterVoice]);

  // There may be inputText on initial mount if there was draft state, so put the caret after it. A new
  // chat also claims focus so the user can type immediately; an existing chat opened from the listing is
  // left in the compose box's resting state until the user clicks in.
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea == null) {
      return;
    }
    if (chatSessionId == null) {
      textarea.focus({preventScroll: true});
    }
    const length = textarea.value.length;
    textarea.setSelectionRange(length, length);
  }, [chatSessionId]);

  const hasFiles = selectedFiles.length > 0;
  const hasMultipleFiles = selectedFiles.length > 1;
  const hasUploadingFiles = selectedFiles.some((f) => f.completedUpload == null);
  const isNewCoScientistChat =
    chatSessionId == null && agentType === AgentType.CO_SCIENTIST && setAgentWorkspaceFolderId != null;
  const isExistingCoScientistChat =
    chatSessionId != null && agentType === AgentType.CO_SCIENTIST && agentWorkspaceFolderId != null;
  // A folder cleared mid-chat (e.g. by revoking full access) can otherwise never be set again -
  // show a picker in its place so the user isn't stuck. Only offered when approvals are enabled:
  // the update-workspace-folder endpoint requires it (a user without approvals has no path to
  // change their folder), so the UI shouldn't offer an affordance the endpoint will reject.
  const isExistingCoScientistChatMissingFolder =
    chatSessionId != null &&
    agentType === AgentType.CO_SCIENTIST &&
    agentWorkspaceFolderId == null &&
    BenchlingConfig.LLM_ENABLE_AGENT_APPROVALS &&
    onSetAgentWorkspaceFolder != null;
  const hasRequiredAgentWorkspaceFolder = !isNewCoScientistChat || agentWorkspaceFolderId != null;
  const isSubmitDisabled =
    !showStopButton &&
    (inputControlsDisabled ||
      (!inputText.trim() && (requireTextForSubmit || !hasFiles)) ||
      hasUploadingFiles ||
      agentType == null ||
      !hasRequiredAgentWorkspaceFolder);
  const isStopDisabled = routineDisabled || isInterrupting || onInterrupt == null;
  const hasContextChips = contextIds != null && contextIds.length > 0;
  const placeholderText =
    agentType == null
      ? 'Pick an agent to begin.'
      : hasContextChips && !showStopButton
        ? CONTEXT_CHAT_INPUT_PLACEHOLDER
        : getAgentTypePlaceholderText(agentType, targetItem, {isAgentStoppable: showStopButton});

  const handleMessageChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    setInputText(e.target.value);
  };

  // Auto-resize the textarea to fit its content. Run as a layout effect keyed on inputText so the
  // height recalculates whenever the value changes, including when inputText is programmatically
  // cleared on submit (draft status -> PENDING). Without this, the previously-computed inline
  // height would persist, leaving a tall empty textarea that covers the message thread and
  // prevents the user from scrolling up. See BNCH bug report: "If my prompt is too long, I can't
  // scroll up anymore after hitting submit".
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }
    if (isPreviewText) {
      textarea.style.height = '0px';
      return;
    }
    // Collapse first so `scrollHeight` reflects current content. Using only `height: auto` can
    // leave a stale large scrollHeight after deleting lines, which blocks the wrapper from shrinking.
    // Hide the scrollbar while measuring: at 0px the content overflows, and a classic scrollbar
    // narrows the text so it wraps onto extra lines that are blank once the final height fits.
    // The `important` priority is required to override the Aphrodite `overflow-y` rule.
    textarea.style.setProperty('overflow-y', 'hidden', 'important');
    textarea.style.height = '0px';
    const scrollHeight = textarea.scrollHeight;
    textarea.style.removeProperty('overflow-y');
    const nextHeight = Math.max(
      textareaMinHeightPx,
      Math.min(CHAT_INPUT_TEXTAREA_MAX_HEIGHT_PX, scrollHeight)
    );
    textarea.style.height = `${nextHeight}px`;
  }, [inputText, isPreviewText, textareaMinHeightPx]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>): void => {
      if (e.key === 'Enter' && !e.shiftKey) {
        // Enter (without shift) should submit the message if possible, and never type a newline.
        e.preventDefault();
        if (showStopButton) {
          if (!isStopDisabled) {
            onInterrupt?.();
          }
        } else if (!isSubmitDisabled) {
          onSubmit();
        }
      }
    },
    [isStopDisabled, isSubmitDisabled, onInterrupt, onSubmit, showStopButton]
  );

  const handleRemoveFile = useCallback(
    (file: File): void => {
      if (!conversationChangesDisabled) {
        const selectedFile = selectedFiles.find((f) => f.file === file);
        if (selectedFile) {
          onRemoveFile(selectedFile.key);
        }
      }
    },
    [conversationChangesDisabled, onRemoveFile, selectedFiles]
  );

  const handleSelectFiles = useCallback(
    (filesToSelect: Array<File>): void => {
      onSelectFiles(filesToSelect);
    },
    [onSelectFiles]
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLTextAreaElement>): void => {
      if (conversationChangesDisabled) {
        return;
      }

      const items = Array.from(e.clipboardData.items);
      // Some apps, such as Numbers, put an empty file on the clipboard alongside copied text. Skip it so the
      // text pastes; an empty file pasted on its own still attaches.
      const hasText = items.some((item) => item.kind === 'string');
      const files = items.flatMap((item) => {
        const file = item.kind === 'file' ? item.getAsFile() : null;
        return file == null || (hasText && file.size === 0) ? [] : [file];
      });
      if (files.length > 0) {
        e.preventDefault();
        handleSelectFiles(files);
      }
    },
    [conversationChangesDisabled, handleSelectFiles]
  );

  const handleFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const files = e.target.files;
      if (!conversationChangesDisabled && files && files.length > 0) {
        handleSelectFiles(Array.from(files));
      }
      // Reset the input value so the same file can be selected again
      e.target.value = '';
    },
    [conversationChangesDisabled, handleSelectFiles]
  );

  const handleDrop = useCallback(
    (e: DragEvent): void => {
      e.preventDefault();
      e.stopPropagation();

      if (conversationChangesDisabled) {
        return;
      }

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        handleSelectFiles(Array.from(files));
      }
    },
    [conversationChangesDisabled, handleSelectFiles]
  );

  const handleSelectSkill = useCallback(
    (skillName: string) => {
      if (isVoiceSessionActive) {
        return;
      }
      const currentText = textareaRef.current?.value ?? '';
      const prefix = currentText.length > 0 && !currentText.endsWith(' ') ? ' ' : '';
      setInputText(`${currentText}${prefix}/${skillName} `);
      textareaRef.current?.focus();
    },
    [isVoiceSessionActive, setInputText]
  );

  useEffect(() => {
    if (!hasMultipleFiles) {
      setAttachmentsExpanded(false);
    }
  }, [hasMultipleFiles]);

  const handleToggleAttachments = useCallback((): void => {
    setAttachmentsExpanded((isExpanded) => !isExpanded);
  }, []);

  const canStartVoice =
    BenchlingConfig.LLM_AGENT_APPROVALS_RELEASED &&
    effectiveVoiceUIController?.state === VoiceUIState.IDLE &&
    !restrictToAgentControls &&
    !showStopButton &&
    // A submitted message disables the composer before the assistant is interruptible. Keeping the
    // send control through that window avoids briefly swapping in a Voice button that cannot start.
    !inputControlsDisabled &&
    inputText.trim().length === 0 &&
    !hasFiles &&
    (chatSessionId != null || onStartVoiceFromNewChat != null) &&
    agentType != null &&
    doesAgentTypeSupportRealtimeVoice(agentType);
  const standardSubmitControl = (
    <Button
      dataTestAttrs={{
        component: 'ChatInput',
        element: showStopButton ? 'stop' : 'submit',
      }}
      aria-label={showStopButton ? 'Stop' : 'Send'}
      buttonStyle={ButtonStyle.PRIMARY}
      size={ButtonSize.MINI}
      className={css(styles.chatToolbarMiniControl)}
      onClick={showStopButton ? onInterrupt : onSubmit}
      disabled={showStopButton ? isStopDisabled : isSubmitDisabled}
      icon={showStopButton ? 'stop' : 'arrow-up'}
    />
  );
  const voiceControl =
    authorizedVoiceUIController == null ? null : (
      <VoiceInputAction
        controller={authorizedVoiceUIController}
        disabled={voiceStartDisabled}
        onStartError={handleVoiceStartError}
        onStopRequested={() => {
          setShouldFocusAfterVoice(true);
        }}
      />
    );
  const submitControl = isVoiceSessionActive ? (
    <div className={voiceClasses.trailingControls}>
      {showStopButton && standardSubmitControl}
      {voiceControl}
    </div>
  ) : isVoiceToolProcessing ? (
    standardSubmitControl
  ) : canStartVoice ? (
    voiceControl
  ) : (
    standardSubmitControl
  );

  const agentToolbarProps: React.ComponentProps<typeof AgentToolbar> = {
    textareaRef,
    fileInputRef,
    cameraInputRef,
    allowedChatTypes,
    agentType,
    setAgentType,
    inputDisabled: inputControlsDisabled,
    publicDataEnabled,
    setPublicDataEnabled,
    citationGenerationMode,
    setCitationGenerationMode,
    selectedMcpServerIds,
    setSelectedMcpServerIds: hideMcpControl ? undefined : setSelectedMcpServerIds,
    fullAccessEnabled: fullAccessEnabled ?? false,
    fullAccessFolderId,
    onGrantFullAccess,
    onRevokeFullAccess,
    onSelectSkill: handleSelectSkill,
    useBackgroundAgentQueue,
    setUseBackgroundAgentQueue,
    restrictToAgentControls,
    hideCitationsControl,
  };

  const showFullActionsNotice =
    chatSessionId != null &&
    fullAccessEnabled === true &&
    isVoiceSessionActive &&
    !dismissedAutomaticFullActionsNotice;

  return (
    <div
      ref={inputContainerRef}
      className={css(styles.inputContainer)}
      {...getTestProps({component: 'ChatInput', element: 'root'})}
    >
      {/* Show this in the created chat, not in the temporary new-chat input.
          Explain Full actions while Voice is active, regardless of how access was enabled.
          Dismissal is saved per user, so the notice stays hidden across chats. */}
      {showFullActionsNotice && (
        <Group
          align="center"
          aria-labelledby={automaticFullActionsNoticeLabelId}
          className={voiceNoticeClasses.notice}
          noWrap={true}
          role="region"
          spacing="md"
          {...getTestProps({component: 'AutomaticFullActionsNotice', element: 'root'})}
        >
          <BenchlingIcon className={voiceNoticeClasses.infoIcon} icon="info-circle" />
          <Text className={voiceNoticeClasses.text} id={automaticFullActionsNoticeLabelId}>
            Full actions were automatically enabled for Voice.
          </Text>
          <ActionIcon
            {...getTestProps({component: 'AutomaticFullActionsNotice', element: 'dismiss'})}
            aria-label="Dismiss notice"
            className={voiceNoticeClasses.dismissButton}
            onClick={() => {
              setDismissedAutomaticFullActionsNotice(true);
            }}
            size="sm"
            variant="subtle"
          >
            <BenchlingIcon icon="xmark" />
          </ActionIcon>
        </Group>
      )}
      {agentTypeError && (
        <div className={css(styles.bannerWrapper)}>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text={agentTypeError.title}
            supportingInfo={agentTypeError.detail}
          />
        </div>
      )}
      <DropTarget
        shouldCaptureDrop={true}
        mimeTypes={['Files']}
        dropEffect="copy"
        targetDesc="chat input"
        onDrop={handleDrop}
      >
        <div className={voiceClasses.inputGroupLayer}>
          {voicePresentationController != null && (
            <VoiceInputDecorations controller={voicePresentationController} />
          )}
          <div
            className={cxVoice(
              css(styles.inputGroup, inputControlsDisabled && styles.inputGroupDisabled),
              isVoicePresentationActive && voiceClasses.inputGroupActive
            )}
            {...getTestProps({component: 'ChatInput', element: 'fileDropZone'})}
          >
            {isNewCoScientistChat && (
              <CoScientistAgentWorkspaceFolderPicker
                selectedAgentWorkspaceFolderId={agentWorkspaceFolderId ?? null}
                onChangeAgentWorkspaceFolderId={setAgentWorkspaceFolderId}
                inputDisabled={inputControlsDisabled}
              />
            )}
            {isExistingCoScientistChat && (
              <CoScientistAgentWorkspaceFolderChip agentWorkspaceFolderId={agentWorkspaceFolderId} />
            )}
            {isExistingCoScientistChatMissingFolder && (
              <ExistingCoScientistAgentWorkspaceFolderPicker
                onSetAgentWorkspaceFolderId={onSetAgentWorkspaceFolder}
                inputDisabled={inputControlsDisabled}
              />
            )}
            {hasContextChips && <ContextChips contextIds={contextIds} onRemove={removeContextId} />}
            {targetItem && !BenchlingConfig.LLM_AGENT_APPROVALS_RELEASED && (
              <TargetItemChip targetItem={targetItem} />
            )}
            <div className={css(styles.inputAreaWrapper)} style={{minHeight: textareaMinHeightPx}}>
              {isVoiceSessionActive && effectiveVoiceUIController != null && (
                <VoiceInputStatus controller={voicePresentationController ?? effectiveVoiceUIController} />
              )}
              {isVoiceToolProcessing && !isVoiceSessionActive && <VoiceToolProcessingStatus />}
              {!isVoicePresentationActive && !isPreviewText && inputText.length === 0 && (
                <div key={placeholderText} className={css(styles.placeholderOverlay)} aria-hidden={true}>
                  {placeholderText}
                </div>
              )}
              {isPreviewText && (
                <div className={css(styles.previewTextOverlay)} aria-hidden={true}>
                  {inputText}
                </div>
              )}
              <textarea
                {...getTestProps({component: 'ChatInput', element: 'textarea'})}
                ref={mergedTextareaRef}
                className={cxVoice(
                  css(styles.input, isPreviewText && styles.inputPreview),
                  isVoicePresentationActive && voiceClasses.inputActive
                )}
                placeholder=""
                aria-placeholder={placeholderText}
                aria-hidden={isVoicePresentationActive || undefined}
                // Only a new chat opens focused; see the caret-placement effect above.
                autoFocus={chatSessionId == null}
                value={inputText}
                onChange={handleMessageChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                disabled={conversationChangesDisabled}
              />
            </div>
            <div className={css(styles.inputGroupButtonRow)}>
              <div className={css(styles.inputGroupIconButtons)}>
                {/* Invisible input to trigger file picker */}
                <input
                  {...getTestProps({component: 'ChatInput', element: 'fileInput'})}
                  type="file"
                  multiple={true}
                  className="hidden"
                  ref={fileInputRef}
                  onChange={handleFileInputChange}
                />
                {/* Invisible input to trigger camera */}
                <input
                  {...getTestProps({component: 'ChatInput', element: 'cameraInput'})}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  ref={cameraInputRef}
                  onChange={handleFileInputChange}
                />
                <AgentToolbar
                  {...agentToolbarProps}
                  voiceSessionActive={isVoiceSessionActive || isVoiceToolProcessing}
                />
                {selectedFiles.length === 1 && (
                  <AttachmentChip
                    selectedFile={selectedFiles[0]}
                    index={0}
                    disabled={inputControlsDisabled}
                    onRemove={handleRemoveFile}
                    classes={attachmentClasses}
                  />
                )}
                {hasMultipleFiles && (
                  <MantineButton
                    {...getTestProps({component: 'ChatInput', element: 'attachmentsToggle'})}
                    aria-controls={attachmentPanelId}
                    aria-expanded={attachmentsExpanded}
                    aria-label={`${attachmentsExpanded ? 'Hide' : 'Show'} ${selectedFiles.length} attachments`}
                    className={cxAttachments(
                      attachmentClasses.summaryButton,
                      attachmentsExpanded && attachmentClasses.summaryButtonExpanded
                    )}
                    compact={true}
                    disabled={inputControlsDisabled}
                    leftIcon={<BenchlingIcon icon="paperclip" />}
                    onClick={handleToggleAttachments}
                    rightIcon={<BenchlingIcon icon={attachmentsExpanded ? 'chevron-up' : 'chevron-down'} />}
                    variant="subtle"
                  >
                    {selectedFiles.length}
                  </MantineButton>
                )}
              </div>
              <Group spacing={8} noWrap={true}>
                {llmEnableAssistantEffortToggle === true &&
                  agentType === AgentType.ASSISTANT &&
                  setEffort != null && (
                    <AssistantModeDropdown
                      effort={effort ?? AssistantEffort.STANDARD}
                      onChange={setEffort}
                      disabled={conversationChangesDisabled}
                    />
                  )}
                {showStopButton ? (
                  <Tooltip label="Stop" openDelay={100} closeDelay={0} withinPortal={true}>
                    <span>{submitControl}</span>
                  </Tooltip>
                ) : (
                  submitControl
                )}
              </Group>
            </div>
            {hasMultipleFiles && attachmentsExpanded && (
              <Box
                aria-label="Attachments"
                className={attachmentClasses.attachmentPanel}
                id={attachmentPanelId}
                role="region"
              >
                <Text className={attachmentClasses.attachmentHeading}>
                  {selectedFiles.length} attachments
                </Text>
                <Box className={attachmentClasses.attachmentList} component="ul">
                  {selectedFiles.map((selectedFile, index) => (
                    <Box
                      className={attachmentClasses.attachmentListItem}
                      component="li"
                      key={selectedFile.key}
                    >
                      <AttachmentChip
                        selectedFile={selectedFile}
                        index={index}
                        disabled={inputControlsDisabled}
                        onRemove={handleRemoveFile}
                        classes={attachmentClasses}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </div>
        </div>
      </DropTarget>
    </div>
  );
}

interface AttachmentStyles {
  attachmentChip: string;
  attachmentFilename: string;
  removeAttachmentButton: string;
}

function AttachmentChip({
  selectedFile,
  index,
  disabled,
  onRemove,
  classes,
}: {
  selectedFile: ChatSelectedFile;
  index: number;
  disabled: boolean;
  onRemove: (file: File) => void;
  classes: AttachmentStyles;
}): JSX.Element {
  const handleRemove = useCallback((): void => {
    onRemove(selectedFile.file);
  }, [onRemove, selectedFile.file]);

  return (
    <Group
      aria-label={`Attachment: ${selectedFile.file.name}`}
      className={classes.attachmentChip}
      noWrap={true}
      role="group"
      spacing={6}
    >
      <BenchlingIcon icon="paperclip" />
      <Text className={classes.attachmentFilename}>{selectedFile.file.name}</Text>
      {selectedFile.completedUpload == null && (
        <Loader aria-label={`Uploading ${selectedFile.file.name}`} size={14} />
      )}
      <ActionIcon
        {...getTestProps({
          component: 'ChatInput',
          element: 'removeAttachment',
          index: String(index),
        })}
        aria-label={`Remove ${selectedFile.file.name}`}
        className={classes.removeAttachmentButton}
        disabled={disabled}
        onClick={handleRemove}
        size="sm"
        variant="transparent"
      >
        <BenchlingIcon icon="close" />
      </ActionIcon>
    </Group>
  );
}

function CoScientistAgentWorkspaceFolderPicker({
  selectedAgentWorkspaceFolderId,
  onChangeAgentWorkspaceFolderId,
  inputDisabled,
}: {
  selectedAgentWorkspaceFolderId: string | null;
  onChangeAgentWorkspaceFolderId: (agentWorkspaceFolderId: string | null) => void;
  inputDisabled: boolean;
}): JSX.Element {
  const handleAgentWorkspaceFolderChange = useCallback(
    (e: {target: {value: string | null}}): void => {
      onChangeAgentWorkspaceFolderId(e.target.value);
    },
    [onChangeAgentWorkspaceFolderId]
  );

  return (
    <div className={css(styles.coScientistAgentWorkspaceSection)}>
      <div className={css(styles.coScientistAgentWorkspaceFolderSelectorWrapper)}>
        <FolderSelector
          allowCreateFolder={true}
          enableRedesign={true}
          noFolderText="Select agent workspace folder"
          readOnly={inputDisabled}
          showInventory={true}
          showNotebook={true}
          unsetOnFetchFail={true}
          usePaginatedDataSource={true}
          value={selectedAgentWorkspaceFolderId}
          onChange={handleAgentWorkspaceFolderChange}
          dataTestAttrs={{component: 'ChatInput', element: 'agentWorkspaceFolderPicker'}}
        />
      </div>
    </div>
  );
}

/**
 * Folder picker shown in place of CoScientistAgentWorkspaceFolderChip for an existing AI Scientist
 * chat that has no workspace folder (e.g. after revoking full access). Unlike
 * CoScientistAgentWorkspaceFolderPicker (new chats, local draft state only), selecting a folder
 * here calls the server immediately via onSetAgentWorkspaceFolderId, since the chat already
 * exists. A 409 (agent running) is surfaced as a retry-able error rather than failing silently.
 */
function ExistingCoScientistAgentWorkspaceFolderPicker({
  onSetAgentWorkspaceFolderId,
  inputDisabled,
}: {
  onSetAgentWorkspaceFolderId: (agentWorkspaceFolderId: string) => Promise<boolean>;
  inputDisabled: boolean;
}): JSX.Element {
  const [isSaving, setIsSaving] = useState(false);

  const handleAgentWorkspaceFolderChange = useCallback(
    async (e: {target: {value: string | null}}): Promise<void> => {
      const folderId = e.target.value;
      if (folderId == null) {
        return;
      }
      setIsSaving(true);
      try {
        await onSetAgentWorkspaceFolderId(folderId);
        // eslint-disable-next-line benchlint/always-throw-from-catch -- terminal UI handler; surface to the user instead of rethrowing
      } catch (error: unknown) {
        notifications.error(
          Net.getUserMessage(error) ?? 'Could not set the workspace folder. Please try again.',
          {id: 'agent-workspace-folder-update-error'}
        );
      } finally {
        setIsSaving(false);
      }
    },
    [onSetAgentWorkspaceFolderId]
  );

  return (
    <div className={css(styles.coScientistAgentWorkspaceSection)}>
      <div className={css(styles.coScientistAgentWorkspaceFolderSelectorWrapper)}>
        <FolderSelector
          allowCreateFolder={true}
          enableRedesign={true}
          noFolderText="Select agent workspace folder"
          readOnly={inputDisabled || isSaving}
          showInventory={true}
          showNotebook={true}
          unsetOnFetchFail={true}
          usePaginatedDataSource={true}
          value={null}
          onChange={handleAgentWorkspaceFolderChange}
          dataTestAttrs={{component: 'ChatInput', element: 'existingAgentWorkspaceFolderPicker'}}
        />
      </div>
    </div>
  );
}

const useAttachmentStyles = createStyles((theme) => ({
  attachmentChip: {
    boxSizing: 'border-box',
    height: CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX,
    maxWidth: 260,
    minWidth: 0,
    paddingLeft: 10,
    paddingRight: 4,
    color: theme.colors.blue[7],
    backgroundColor: theme.colors.blue[0],
    border: `1px solid ${theme.colors.blue[2]}`,
    borderRadius: theme.radius.sm,
  },
  attachmentFilename: {
    minWidth: 0,
    overflow: 'hidden',
    fontSize: theme.fontSizes.sm,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  removeAttachmentButton: {
    flexShrink: 0,
    color: theme.colors.blue[7],
    '&:hover': {
      backgroundColor: theme.colors.blue[1],
    },
  },
  summaryButton: {
    boxSizing: 'border-box',
    height: CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX,
    minHeight: CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX,
    paddingLeft: 8,
    paddingRight: 8,
    color: theme.colors.blue[7],
    backgroundColor: theme.colors.blue[0],
    border: `1px solid ${theme.colors.blue[2]}`,
    borderRadius: theme.radius.sm,
    '&:hover': {
      backgroundColor: theme.colors.blue[1],
    },
  },
  summaryButtonExpanded: {
    color: theme.white,
    backgroundColor: theme.colors.blue[7],
    borderColor: theme.colors.blue[7],
    '&:hover': {
      backgroundColor: theme.colors.blue[8],
    },
  },
  attachmentPanel: {
    padding: `${theme.spacing.sm} ${theme.spacing.md} ${theme.spacing.md}`,
    borderTop: `1px solid ${theme.colors.gray[2]}`,
  },
  attachmentHeading: {
    marginBottom: theme.spacing.xs,
    color: theme.colors.gray[7],
    fontSize: theme.fontSizes.xs,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  attachmentList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing.xs,
    padding: 0,
    margin: 0,
    listStyle: 'none',
  },
  attachmentListItem: {
    minWidth: 0,
  },
}));

const useVoiceLayoutStyles = createStyles(() => ({
  inputGroupLayer: {
    position: 'relative',
    width: '100%',
    isolation: 'isolate',
  },
  inputGroupActive: {
    position: 'relative',
    zIndex: 2,
    overflow: 'visible',
  },
  inputActive: {
    opacity: 0,
    pointerEvents: 'none',
  },
  trailingControls: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
}));

const useVoiceNoticeStyles = createStyles((theme) => ({
  notice: {
    // Keep the notice above the cloud and dots in the input's isolated animation layer.
    position: 'relative',
    zIndex: 1,
    width: '100%',
    minWidth: 0,
    // Match ProgressiveDisclosure's small category pills; allow taller rows if text wraps.
    minHeight: 28,
    padding: `6px ${theme.spacing.md}`,
    marginBottom: theme.spacing.sm,
    color: theme.colors.gray[9],
    backgroundColor: theme.colors.blue[0],
    borderLeft: `4px solid ${theme.colors.blue[7]}`,
    borderRadius: theme.radius.sm,
  },
  infoIcon: {
    // Override Font Awesome's 1em sizing regardless of stylesheet load order.
    '&&': {
      width: 16,
      height: 16,
    },
    flexShrink: 0,
    color: theme.colors.blue[7],
  },
  text: {
    minWidth: 0,
    flex: 1,
    overflowWrap: 'anywhere',
    fontSize: theme.fontSizes.md,
    fontWeight: theme.other.fontWeights.normal,
    lineHeight: '14px',
  },
  dismissButton: {
    flexShrink: 0,
    // Keep the 28px hit target inside the padded row without increasing its height.
    marginTop: -6,
    marginBottom: -6,
    color: theme.colors.gray[5],
    '& svg': {
      width: 16,
      height: 16,
    },
  },
}));

const styles = StyleSheet.create({
  inputContainer: {
    width: '100%',
    maxWidth: CHAT_CONTENT_MAX_WIDTH_PX,
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '4px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    border: `1px solid ${AI_GRAY_200}`,
    borderRadius: '24px',
    backgroundColor: 'rgb(255, 255, 255)',
    transition: 'border-color 0.2s ease-in-out, box-shadow 0.25s ease-in-out',
    boxShadow: 'rgb(0 13 181 / 2%) 0px 0px 22px 0px',
    ':hover': {
      borderColor: Color.GRAY_300,
    },
    ':focus-within': {
      border: '1px solid rgba(90, 122, 236, 0.26)',
      boxShadow: 'rgb(0 13 181 / 5%) 0px 0px 30px 0px',
    },
  },
  inputGroupDisabled: {
    backgroundColor: GRAY_LIGHTER,
    ':hover': {
      borderColor: AI_GRAY_200,
    },
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    // The placeholder is a sibling overlay rather than the native attribute, and a positioned element
    // would otherwise paint over the caret at character 0. Lifting the (transparent) textarea into the
    // same layer keeps the caret visible while the placeholder still shows through behind it.
    position: 'relative',
    zIndex: 1,
    // Cap the textarea height so very long prompts don't push the rest of the input
    // (file list, toolbar, send button) off-screen or cover the message thread. Beyond
    // this cap, the textarea becomes internally scrollable via overflow-y: auto below.
    maxHeight: `${CHAT_INPUT_TEXTAREA_MAX_HEIGHT_PX}px`,
    minHeight: 0,
    padding: '12px 16px',
    fontFamily: 'inherit',
    fontSize: '16px',
    lineHeight: '24px',
    border: 'none',
    background: 'transparent',
    resize: 'none',
    overflowY: 'auto',
    ':focus': {
      outline: 'none',
    },
  },
  inputAreaWrapper: {
    position: 'relative',
    width: '100%',
  },
  placeholderOverlay: {
    position: 'absolute',
    left: '16px',
    right: '16px',
    top: '12px',
    pointerEvents: 'none',
    color: AI_GRAY_425,
    fontSize: '16px',
    fontFamily: 'inherit',
    lineHeight: '24px',
    overflow: 'hidden',
    animationName: [
      {
        from: {opacity: 0},
        to: {opacity: 1},
      },
    ],
    animationDuration: '450ms',
    animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)', // ease-in-out
  },
  inputPreview: {
    position: 'absolute',
    opacity: 0,
    pointerEvents: 'none',
    overflow: 'hidden',
  },
  previewTextOverlay: {
    padding: '12px 16px 0',
    maxHeight: '60px',
    fontSize: '16px',
    fontFamily: 'inherit',
    lineHeight: '24px',
    color: AI_GRAY_425,
    display: '-webkit-box' as CSSPropertiesComplete['display'],
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    wordBreak: 'break-word',
  },
  inputGroupButtonRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '10px 12px 12px 12px',
  },
  inputGroupIconButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: '8px',
    minWidth: 0,
  },
  /**
   * Draws the send control at chat-toolbar size while reserving the larger Voice footprint through a
   * transparent gutter, so swapping between send and Voice keeps the composer height and the
   * control's center fixed.
   */
  chatToolbarMiniControl: {
    boxSizing: 'border-box',
    minHeight: `${CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX}px`,
    height: `${CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX}px`,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: `${CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX}px`,
    margin: `${SEND_CONTROL_GUTTER_PX}px`,
    borderRadius: '12px',
  },
  warningMessage: {
    fontSize: '12px',
    color: Color.GRAY_700,
    textAlign: 'center',
    marginTop: '4px',
    marginBottom: '8px',
  },
  bannerWrapper: {
    marginBottom: '8px',
  },
  coScientistAgentWorkspaceSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  coScientistAgentWorkspaceFolderSelectorWrapper: {
    maxWidth: 260,
    padding: '12px 12px 0',
  },
});
